import React, { useEffect, useState } from 'react';
import { useModal } from 'react-modal-hook';
import { useDispatch, useSelector } from 'react-redux';
import { AlertContent, ModalFactory } from 'components';
import { selectAPIRequestStatus, selectIsLoadingAPIRequest } from 'store/ApiClient';
import { getPosCategoriesRequest, POSCategories } from 'store/Collections';
import { selectSiteSettings } from 'store/Site';
import { IStore } from 'store/types';
import { IFormCalculatorRule, IFormValues, RequestStatus, TipCalculatorRuleConfiguration } from 'types';
import { sortAlphaNumerically } from 'utils';
import { capitalize } from 'utils/strings';
import { AddButton, TipCalculationsContainer } from '../../styles';
import { CategoriesLoader } from '../components/CategoriesLoader';
import { CreateTipShare } from '../components/CreateTipShare';
import { MoreInfoButton, PermissionTooltip, TipCalculationsDescription } from '../components/styles';
import { TipCalculationsMethodInfoModal } from '../components/TipCalculationsMethodInfoModal';
import { TipShareItem } from '../components/TipShareItem';

interface ITipSharesFormProps {
  siteId: string;
  activeRules?: IFormValues;
  isLoading?: boolean;
  onConfirm: (values: IFormValues, onSuccess?: () => void) => void;
  editPermission?: boolean;
  tabNav: React.ReactNode;
}

export const TipSharesForm: React.FC<ITipSharesFormProps> = ({ siteId, activeRules, isLoading, onConfirm, editPermission, tabNav }) => {
  const dispatch = useDispatch();
  const { tipCalculatorRuleConfiguration } = useSelector(selectSiteSettings()) || {};
  const isAdvanced = tipCalculatorRuleConfiguration === TipCalculatorRuleConfiguration.ADVANCED;
  const isLoadingPosCategories = useSelector((state: IStore) => selectIsLoadingAPIRequest(state, POSCategories.GET_POS_CATEGORIES_REQUEST));
  const getPosCategoriesStatus = useSelector((state: IStore) => selectAPIRequestStatus(state, POSCategories.GET_POS_CATEGORIES_REQUEST));
  const [selectedRule, setSelectedRule] = useState<IFormCalculatorRule>();
  const [showLoader, setShowLoader] = useState(false);

  const [showCreateTipShare, hideCreateTipShare] = useModal(
    () => <CreateTipShare hideModal={hideCreateTipShare} initialValues={activeRules} isAdvanced={isAdvanced} onConfirm={onConfirm} siteId={siteId} isLoading={isLoading} selectedRule={selectedRule} />,
    [activeRules, isAdvanced, isLoading, onConfirm, selectedRule]
  );

  const [showPosCategoriesLoader, hidePosCategoriesLoader] = useModal(() => <CategoriesLoader />, []);

  const loadPosCategories = () => {
    showPosCategoriesLoader();
    dispatch(getPosCategoriesRequest(siteId));
    setShowLoader(true);
  };

  const onEditTipShare = (rule: IFormCalculatorRule) => {
    setSelectedRule(rule);
    loadPosCategories();
  };

  const onCreateTipShare = () => {
    setSelectedRule(undefined);
    loadPosCategories();
  };

  useEffect(() => {
    if (!isLoadingPosCategories && showLoader) {
      hidePosCategoriesLoader();
      setShowLoader(false);

      if (getPosCategoriesStatus === RequestStatus.SUCCESS) {
        showCreateTipShare();
      }
    }
  }, [isLoadingPosCategories]);

  const [showModal, hideModal] = useModal(() => <TipCalculationsMethodInfoModal hideModal={hideModal} />, []);

  const [showDeleteModal, hideDeleteModal] = useModal(() => {
    const onDelete = () => {
      const filteredRules = activeRules.rules.filter((rule) => rule.id !== selectedRule.id);
      onConfirm({ ...activeRules, rules: filteredRules }, hideDeleteModal);
    };

    return (
      <ModalFactory customModal onClose={hideDeleteModal} isActive>
        <AlertContent
          title={`Remove ${capitalize(selectedRule.contributor.name)} Share?`}
          message="This will delete all of the rules associated with this tip share and cannot be undone."
          leftActionLabel="Cancel"
          rightActionLabel="Remove"
          onClose={hideDeleteModal}
          onLeftAction={hideDeleteModal}
          onRightAction={onDelete}
          isLoading={isLoading}
          rightActionAppearance="destructive"
        />
      </ModalFactory>
    );
  }, [activeRules, selectedRule, isLoading, onConfirm]);

  const handleDelete = (rule: IFormCalculatorRule) => {
    setSelectedRule(rule);
    showDeleteModal();
  };

  return (
    <TipCalculationsContainer data-cy="tip-shares-form">
      {tabNav}
      <TipCalculationsDescription size="xsmall">
        {'Configure your tip sharing policy by creating rules for each contributing job. To set up multiple contributing jobs, you can create multiple tip shares. '}
        <MoreInfoButton onClick={showModal}>Click here for more information.</MoreInfoButton>
      </TipCalculationsDescription>
      {activeRules.rules
        .filter((rule) => !!rule?.sharePolicy && !rule.default)
        .sort(sortAlphaNumerically('contributor.name'))
        .map((rule) => (
          <TipShareItem key={rule.id} tipShare={rule} onEdit={() => onEditTipShare(rule)} onDelete={() => handleDelete(rule)} isAdvanced={isAdvanced} disabled={!editPermission} />
        ))}
      <AddButton appearance="white" icon="Plus" label="Create Tip Share" data-cy="create-tip-share-button" onClick={onCreateTipShare} disabled={!editPermission} tooltipId="no-edit-permission" />
      {!editPermission && <PermissionTooltip type="edit" />}
    </TipCalculationsContainer>
  );
};

export default TipSharesForm;
